import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { 
  ArrowLeftIcon,
  UserIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import api from '../../../services/api';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

interface BusinessUser {
  id: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  businessType: string;
  businessStatus: string;
  role: string;
  isSystemSuperAdmin: boolean;
  createdAt: string;
  lastLoginAt?: string;
  places?: {
    id: string;
    name: string;
    role: string;
  }[];
}

interface Place {
  id: string;
  name: string;
  city: string;
  state: string;
}

interface UpdateBusinessUserRequest {
  fullName: string;
  phoneNumber?: string;
  status: string;
  businessUserRole: string;
}

interface LinkPlaceRequest {
  placeId: string;
  role: string;
}

const BusinessUserEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formData, setFormData] = useState<UpdateBusinessUserRequest>({
    fullName: '',
    phoneNumber: '',
    status: 'Pending',
    businessUserRole: 'CafeOwner'
  });

  const [linkForm, setLinkForm] = useState<LinkPlaceRequest>({
    placeId: '',
    role: 'Owner'
  });

  // Buscar usuário
  const { data: businessUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['business-user', id],
    queryFn: async () => {
      const response = await api.get(`/admin/business-users/${id}`);
      return response.data as BusinessUser;
    },
    enabled: !!id
  });

  // Buscar cafeterias disponíveis
  const { data: places, isLoading: isLoadingPlaces } = useQuery({
    queryKey: ['places'],
    queryFn: async () => {
      const response = await api.get('/admin/places');
      return response.data.places as Place[];
    }
  });

  // Atualizar dados quando o usuário for carregado
  useEffect(() => {
    if (businessUser) {
      setFormData({
        fullName: businessUser.fullName,
        phoneNumber: businessUser.phoneNumber || '',
        status: businessUser.businessStatus,
        businessUserRole: businessUser.role
      });
    }
  }, [businessUser]);

  // Mutação para atualizar usuário
  const updateBusinessUserMutation = useMutation({
    mutationFn: async (data: UpdateBusinessUserRequest) => {
      const response = await api.put(`/admin/business-users/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      alert('Usuário atualizado com sucesso!');
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar usuário:', error);
      alert('Erro ao atualizar usuário empresarial');
    }
  });

  // Mutação para vincular cafeteria
  const linkPlaceMutation = useMutation({
    mutationFn: async (data: LinkPlaceRequest) => {
      const response = await api.post(`/admin/business-users/${id}/link-place`, data);
      return response.data;
    },
    onSuccess: () => {
      // Refetch do usuário para atualizar a lista de cafeterias
      window.location.reload();
    },
    onError: (error: any) => {
      console.error('Erro ao vincular cafeteria:', error);
      alert('Erro ao vincular cafeteria');
    }
  });

  // Mutação para desvincular cafeteria
  const unlinkPlaceMutation = useMutation({
    mutationFn: async (placeId: string) => {
      const response = await api.delete(`/admin/business-users/${id}/unlink-place/${placeId}`);
      return response.data;
    },
    onSuccess: () => {
      // Refetch do usuário para atualizar a lista de cafeterias
      window.location.reload();
    },
    onError: (error: any) => {
      console.error('Erro ao desvincular cafeteria:', error);
      alert('Erro ao desvincular cafeteria');
    }
  });

  const handleInputChange = (field: keyof UpdateBusinessUserRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateBusinessUserMutation.mutate(formData);
  };

  const handleLinkPlace = (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkForm.placeId) {
      alert('Selecione uma cafeteria');
      return;
    }
    linkPlaceMutation.mutate(linkForm);
  };

  const handleUnlinkPlace = (placeId: string) => {
    if (confirm('Tem certeza que deseja desvincular esta cafeteria?')) {
      unlinkPlaceMutation.mutate(placeId);
    }
  };

  if (isLoadingUser || isLoadingPlaces) {
    return <LoadingSpinner />;
  }

  if (!businessUser) {
    return <div>Usuário não encontrado</div>;
  }

  // Filtrar cafeterias que não estão vinculadas
  const availablePlaces = places?.filter(place => 
    !businessUser.places?.some(userPlace => userPlace.id === place.id)
  ) || [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/business-users')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Voltar para Usuários Empresariais
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Editar Usuário Empresarial</h1>
        <p className="text-gray-600">Edite as informações e permissões do usuário</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Formulário de Edição */}
        <div className="bg-white rounded-lg shadow">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Dados Básicos */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                Dados Básicos
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    disabled
                    value={businessUser.email}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email não pode ser alterado</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>

            {/* Status e Permissões */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <ShieldCheckIcon className="h-5 w-5 mr-2 text-purple-600" />
                Status e Permissões
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="Pending">Pendente</option>
                    <option value="Active">Ativo</option>
                    <option value="Inactive">Inativo</option>
                    <option value="Suspended">Suspenso</option>
                  </select>
                </div>
                
                                 
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Função *
                    </label>
                    <select
                      required
                      value={formData.businessUserRole}
                      onChange={(e) => handleInputChange('businessUserRole', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="CafeOwner">Proprietário de Cafeteria</option>
                      <option value="SystemAdmin">Administrador do Sistema</option>
                      <option value="LegalAdmin">Administrador Legal</option>
                      <option value="SupportAdmin">Administrador de Suporte</option>
                                         </select>
                   </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={updateBusinessUserMutation.isPending}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                {updateBusinessUserMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>

        {/* Gestão de Cafeterias */}
        <div className="space-y-6">
          {/* Cafeterias Vinculadas */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2 text-green-600" />
                Cafeterias Vinculadas
              </h3>
              
              {businessUser.places && businessUser.places.length > 0 ? (
                <div className="space-y-3">
                  {businessUser.places.map((place) => (
                    <div key={place.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{place.name}</p>
                        <p className="text-sm text-gray-600">Função: {place.role}</p>
                      </div>
                      <button
                        onClick={() => handleUnlinkPlace(place.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Desvincular cafeteria"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  Nenhuma cafeteria vinculada
                </p>
              )}
            </div>
          </div>

          {/* Vincular Nova Cafeteria */}
          {availablePlaces.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <PlusIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Vincular Nova Cafeteria
                </h3>
                
                <form onSubmit={handleLinkPlace} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cafeteria
                    </label>
                    <select
                      required
                      value={linkForm.placeId}
                      onChange={(e) => setLinkForm(prev => ({ ...prev, placeId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="">Selecione uma cafeteria</option>
                      {availablePlaces.map((place) => (
                        <option key={place.id} value={place.id}>
                          {place.name} ({place.city}, {place.state})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Função na Cafeteria
                    </label>
                    <select
                      required
                      value={linkForm.role}
                      onChange={(e) => setLinkForm(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="Owner">Proprietário</option>
                      <option value="Admin">Administrador</option>
                      <option value="Manager">Gerente</option>
                    </select>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={linkPlaceMutation.isPending}
                    className="btn btn-primary w-full"
                  >
                    {linkPlaceMutation.isPending ? 'Vinculando...' : 'Vincular Cafeteria'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessUserEditPage;
